import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const POPUP_DELAY_MS = 25000; // 25 saniye sonra tetiklenebilir
const SESSION_KEY = 'exit_popup_shown';

export default function ExitIntentPopup() {
  const [visible, setVisible] = useState(false);
  const [canShow, setCanShow] = useState(false);
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Oturum başına bir kez göster
  const alreadyShown = sessionStorage.getItem(SESSION_KEY) === 'true';

  useEffect(() => {
    if (alreadyShown) return;

    timerRef.current = setTimeout(() => {
      setCanShow(true);
    }, POPUP_DELAY_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [alreadyShown]);

  const handleMouseLeave = useCallback(
    (e: MouseEvent) => {
      if (!canShow || alreadyShown) return;
      // Fare ekranın üst kısmından çıkıyorsa (sekme değiştirme / yeni sekme)
      if (e.clientY <= 10) {
        setVisible(true);
        sessionStorage.setItem(SESSION_KEY, 'true');
      }
    },
    [canShow, alreadyShown]
  );

  useEffect(() => {
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [handleMouseLeave]);

  const handleClose = () => setVisible(false);

  const handleCTA = () => {
    setVisible(false);
    navigate('/urunler?kategori=Taze+Yaprak');
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl overflow-hidden w-full max-w-md animate-[fadeInScale_0.3s_ease-out]">
        {/* Kırmızı başlık */}
        <div className="bg-gradient-to-r from-red-600 to-red-500 px-6 py-4 text-center">
          <div className="flex items-center justify-center gap-2 text-white font-bold text-lg">
            <i className="ri-alarm-warning-line text-yellow-300 text-xl"></i>
            <span>DUR! GİTME!</span>
            <i className="ri-alarm-warning-line text-yellow-300 text-xl"></i>
          </div>
          <p className="text-red-100 text-sm mt-0.5">Bu fırsatları kaçıracaksın</p>
        </div>

        {/* Kapat butonu */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 text-white transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-base"></i>
        </button>

        {/* İçerik */}
        <div className="px-6 py-6">
          {/* İkon + başlık */}
          <div className="text-center mb-5">
            <div className="w-14 h-14 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-3">
              <i className="ri-percent-line text-3xl text-green-600"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900">Sana Özel Son Bir Fırsat!</h3>
            <p className="text-sm text-gray-500 mt-1">
              Şimdi sipariş ver, <span className="text-red-500 font-semibold">2026 hasadı taze yaprak</span> kazan
            </p>
          </div>

          {/* Avantaj kartları */}
          <div className="space-y-2.5 mb-6">
            <div className="flex items-start gap-3 bg-green-50 rounded-xl px-4 py-3">
              <div className="w-8 h-8 flex items-center justify-center shrink-0">
                <i className="ri-gift-line text-green-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">5 kg Alana Kargo Bedava</p>
                <p className="text-xs text-gray-500">5 kg pakette kargo ücreti yok</p>
              </div>
            </div>

            <div className="flex items-start gap-3 bg-amber-50 rounded-xl px-4 py-3">
              <div className="w-8 h-8 flex items-center justify-center shrink-0">
                <i className="ri-leaf-line text-amber-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">2026 Yılı Hasadı</p>
                <p className="text-xs text-gray-500">Manisa Sultaniye — tüysüz, damarsız, mayhoş</p>
              </div>
            </div>

            <div className="flex items-start gap-3 bg-red-50 rounded-xl px-4 py-3">
              <div className="w-8 h-8 flex items-center justify-center shrink-0">
                <i className="ri-price-tag-3-line text-red-500 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">Ön Sipariş Fiyatı</p>
                <p className="text-xs text-gray-500">Bu yılın en dip fiyatları — stok sınırlı</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={handleCTA}
            className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-sm"
          >
            Fırsatları Gör
            <i className="ri-arrow-right-line text-base"></i>
          </button>

          <button
            onClick={handleClose}
            className="w-full text-center text-xs text-gray-400 hover:text-gray-600 mt-3 cursor-pointer transition-colors py-1"
          >
            Hayır, fırsatları kaçırmak istiyorum
          </button>
        </div>
      </div>
    </div>
  );
}
